import { useTranslation } from "react-i18next";
import { alpha, Box, Stack, styled, Typography } from "~/common/mui";
import type { PriceData } from "~/domains/pricing/types/PriceData";
import * as PriceUtils from "~/utils/PriceUtils";
import { CheckoutButton } from "~/features/cart/cart/components/Buttons/CheckoutButton";
import { useAppConfig } from "~/app/config/useAppConfig";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { Param } from "~/features/orders/common/components/v2/Params";
import { ELEVATION_2 } from "~/common/components/icon/Elevation";

const Root = styled(Box)(({ theme }) => ({
  display: "none",
  position: "fixed",
  right: 0,
  bottom: 0,
  left: 0,
  zIndex: theme.zIndex.appBar,
  padding: theme.spacing(1, 3),
  backgroundColor: alpha(theme.palette.surface.bright, 0.95),
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
  borderTop: `1px solid ${theme.palette.divider}`,
  boxShadow: ELEVATION_2,
  [theme.breakpoints.up("desktop-s")]: {
    display: "block",
  },
}));

const Content = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  maxWidth: theme.breakpoints.values["desktop-l"],
  margin: "0 auto",
  alignItems: "center",
  justifyContent: "space-between",
}));

const LeftSection = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  flex: 1,
  gap: theme.spacing(3),
}));

const RightSection = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  alignItems: "center",
  gap: theme.spacing(3),
}));

interface Props {
  accountNumber: string;
  productAmount: number;
  totalItemCount: number;
  totalSum: PriceData | null;
  disabled?: boolean;
}

export const StickyCheckoutBar = ({ accountNumber, productAmount, totalItemCount, totalSum, disabled }: Props) => {
  const { t } = useTranslation();
  const webservicesDisabled = useAppConfig()?.configuration.disableAllWS ?? false;

  const zeroPriceText =
    !userHasPermission(Permission.ADD_TO_QUOTE) && !webservicesDisabled ? t("product.priceOnRequest") : "-";
  const totalSumText = totalSum ? PriceUtils.text2(totalSum, zeroPriceText) : "...";

  return (
    <Root>
      <Content>
        <LeftSection>
          <Param label={t("cartPage.stickyCheckout.accountNumber")} value={accountNumber} />
          <Param label={t("cartPage.stickyCheckout.totalProducts")} value={productAmount} />
          <Param label={t("cartPage.stickyCheckout.totalQuantity")} value={totalItemCount} />
        </LeftSection>
        <RightSection>
          <Stack sx={{ gap: 0.5 }}>
            <Typography variant="bodySmall" sx={{ color: "textLabel.main" }}>
              {t("cartPage.stickyCheckout.netSum")}
            </Typography>
            <Typography variant="titleLarge" sx={{ fontWeight: "bold" }}>
              {totalSumText}
            </Typography>
          </Stack>
          <CheckoutButton disabled={disabled} />
        </RightSection>
      </Content>
    </Root>
  );
};
